import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'

/**
 * Rooms and bookings.
 *
 * `useAvailability` is advisory and says so: it answers what is free *now*, and
 * between that answer and the save somebody else can take the room. The booking
 * request is what decides, and a 409 from it is a normal outcome rather than an
 * error to be surprised by.
 */

export const facilityKeys = {
  all: () => ['facilities'] as const,
  resources: (filters: Record<string, unknown> = {}) =>
    [...facilityKeys.all(), 'resources', filters] as const,
  bookings: (filters: Record<string, unknown> = {}) =>
    [...facilityKeys.all(), 'bookings', filters] as const,
  availability: (query: Record<string, unknown>) =>
    [...facilityKeys.all(), 'availability', query] as const,
}

export function useResources(filters: { campusId?: string; kind?: 'room' | 'equipment' } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: facilityKeys.resources(filters),
    queryFn: async () => {
      const { data, error } = await api.api.v1.facilities.resources.get({ query: filters })
      if (error) throw error
      return data
    },
  })
}

export type FacilityResource = NonNullable<ReturnType<typeof useResources>['data']>[number]

export function useBookings(
  filters: {
    resourceId?: string
    from?: string
    to?: string
    status?: 'pending' | 'approved' | 'rejected' | 'released'
  } = {},
) {
  const api = useApi()
  return useQuery({
    queryKey: facilityKeys.bookings(filters),
    queryFn: async () => {
      const { data, error } = await api.api.v1.facilities.bookings.get({ query: filters })
      if (error) throw error
      return data
    },
  })
}

export type Booking = NonNullable<ReturnType<typeof useBookings>['data']>[number]

function useFacilityInvalidation() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: facilityKeys.all() })
}

export function useCreateResource() {
  const api = useApi()
  const invalidate = useFacilityInvalidation()
  return useMutation({
    mutationFn: async (body: {
      name: string
      kind?: 'room' | 'equipment'
      campusId?: string | null
      description?: string | null
      capacity?: number | null
      isBookable?: boolean
      requiresApproval?: boolean
    }) => {
      const { data, error } = await api.api.v1.facilities.resources.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })
}

export function useBookRoom() {
  const api = useApi()
  const invalidate = useFacilityInvalidation()
  return useMutation({
    mutationFn: async (body: {
      resourceId: string
      purpose: string
      startsAt: string
      endsAt: string
      notes?: string | null
      eventInstanceId?: string | null
      /** How many, for equipment. */
      quantity?: number | null
    }) => {
      const { data, error } = await api.api.v1.facilities.bookings.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })
}

/** Approve or reject a pending request — the facilities manager's inbox. */
export function useDecideBooking() {
  const api = useApi()
  const invalidate = useFacilityInvalidation()
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string
      decision: 'approve' | 'reject'
      note?: string | null
    }) => {
      const { data, error } = await api.api.v1.facilities.bookings({ id }).decision.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })
}

export function useReleaseBooking() {
  const api = useApi()
  const invalidate = useFacilityInvalidation()
  return useMutation({
    mutationFn: async (bookingId: string) => {
      const { error } = await api.api.v1.facilities.bookings({ id: bookingId }).delete()
      if (error) throw error
    },
    onSuccess: () => void invalidate(),
  })
}

export function useUpdateResource() {
  const api = useApi()
  const invalidate = useFacilityInvalidation()
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string
      name?: string
      campusId?: string | null
      description?: string | null
      capacity?: number | null
      isBookable?: boolean
      requiresApproval?: boolean
    }) => {
      const { data, error } = await api.api.v1.facilities.resources({ id }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })
}

/** Retires a room. The server refuses one that still has bookings. */
export function useRemoveResource() {
  const api = useApi()
  const invalidate = useFacilityInvalidation()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.api.v1.facilities.resources({ id }).delete()
      if (error) throw error
    },
    onSuccess: () => void invalidate(),
  })
}

/** Moves a booking. A clash is a 409 naming what has the room. */
export function useRescheduleBooking() {
  const api = useApi()
  const invalidate = useFacilityInvalidation()
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string
      startsAt: string
      endsAt: string
      quantity?: number
    }) => {
      const { data, error } = await api.api.v1.facilities.bookings({ id }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void invalidate(),
  })
}
